import moment from 'moment-timezone';

import type { IService, TFlags, TFlagsRecord } from '@feathr/blackbox';
import { CampaignClass, EServiceItemIds } from '@feathr/blackbox';

import type { TLicenseFeatures } from './useLicense.types';

type TExceptionHandler = (props: IExceptionProps) => TLicenseFeatures;

interface IExceptionProps {
  activeServices: IService[];
  license: TLicenseFeatures;
  flags: TFlags[];
}

interface ILicenseExceptionHandler {
  /** Condition that determines if the exception handler should be applied */
  applies: ({
    services,
    flags,
  }: {
    services: IService[];
    flags?: Array<keyof TFlagsRecord>;
  }) => boolean;
  /** Function to apply exception to the license */
  handle: TExceptionHandler;
  /** Description of what this exception handler does */
  description: string;
}

const createOnboardingHandler =
  (serviceItemId: EServiceItemIds): TExceptionHandler =>
  ({ activeServices, license }) => {
    const activeOnboardingService = activeServices.find((s) => s.item_id === serviceItemId);

    if (activeOnboardingService) {
      const { grace_period, period } = activeOnboardingService;
      const { start, end } = period;
      const now = moment();

      const effectiveStart = moment(start).subtract(grace_period?.start || 0, 'days');
      const effectiveEnd = moment(end).add(grace_period?.end || 0, 'days');

      if (now.isBetween(effectiveStart, effectiveEnd, 'day', '[]')) {
        return {
          ...license,
          implementationsPageVisible: true,
        };
      }
    }

    return license;
  };

const handlePremiumOnboarding: TExceptionHandler = createOnboardingHandler(
  EServiceItemIds.PremiumOnboarding,
);
const handleStandardOnboarding: TExceptionHandler = createOnboardingHandler(
  EServiceItemIds.StandardOnboarding,
);

/**
 * Until now, accounts had unrestricted access to the Implementations page.
 * This flag allows us to override the package-based access control for customers
 * who have been using the feature and have an active package that does not
 * include access.
 */
const handleOverridePackageImplementationsAccess: TExceptionHandler = ({ license, flags }) => {
  if (flags.includes('overridePackageImplementationsAccess')) {
    return {
      ...license,
      implementationsPageVisible: true,
    };
  }
  return license;
};

/**
 * This flag allows us to override the package-based access control for Drip campaigns.
 * Covers both the Pinpoint and Mailrail drip classes so flagged accounts keep drip
 * access on either email backend.
 */
const handleOverrideDripCampagns: TExceptionHandler = ({ license, flags }) => {
  if (flags.includes('showDripCampaigns')) {
    // https://github.com/Feathr/shrike/wiki/Account-Licenses#drip-campaigns
    return {
      ...license,
      [CampaignClass.Drip]: true,
      [CampaignClass.MailrailDrip]: true,
      drip: true,
      mailrailDrip: true,
    };
  }
  return license;
};

/**
 * Accounts with an appropriate active service will have the Implementations page visible
 */
const handleActiveService: TExceptionHandler = ({ activeServices, license }) => {
  if (
    activeServices.some((s) =>
      [
        EServiceItemIds.CampaignManagement,
        EServiceItemIds.EmailSwitchServiceKit,
        EServiceItemIds.ManagedServicesGoogleAdGrantManagement,
        EServiceItemIds.CampaignManagementServicesGoogleAdGrant,
        EServiceItemIds.DataIntegration,
        EServiceItemIds.CreativeMigrationPack,
        EServiceItemIds.Training,
      ].includes(s.item_id),
    )
  ) {
    return {
      ...license,
      implementationsPageVisible: true,
    };
  }
  return license;
};

/**
 * This flag allows us to override the package-based access control for Pro Resources.
 */
const handleOverrideProResources: TExceptionHandler = ({ license, flags }) => {
  if (flags.includes('showProResources')) {
    return {
      ...license,
      proResourcesPageVisible: true,
    };
  }
  return license;
};

// Add new exception handlers here 🙏
const exceptionHandlers: ILicenseExceptionHandler[] = [
  {
    applies: ({ services }) =>
      services.some((s) => s.item_id === EServiceItemIds.PremiumOnboarding),
    handle: handlePremiumOnboarding,
    description:
      'Enables Implementations page for accounts with Premium Onboarding service for first 90 days',
  },
  {
    applies: ({ services }) =>
      services.some((s) => s.item_id === EServiceItemIds.StandardOnboarding),
    handle: handleStandardOnboarding,
    description:
      'Enables Implementations page for accounts with Standard Onboarding service for first 60 days',
  },
  {
    applies: ({ services }) =>
      services.some((s) =>
        [
          EServiceItemIds.CampaignManagement,
          EServiceItemIds.CampaignManagementServicesGoogleAdGrant,
          EServiceItemIds.CreativeMigrationPack,
          EServiceItemIds.DataIntegration,
          EServiceItemIds.EmailSwitchServiceKit,
          EServiceItemIds.ManagedServicesGoogleAdGrantManagement,
          EServiceItemIds.Training,
        ].includes(s.item_id),
      ),
    handle: handleActiveService,
    description: 'Enables Implementations page for accounts with an appropriate active service',
  },
  {
    applies: ({ flags }) => flags?.includes('overridePackageImplementationsAccess') ?? false,
    handle: handleOverridePackageImplementationsAccess,
    description:
      'Enables Implementations page for accounts with the overridePackageImplementationsAccess flag',
  },
  {
    applies: ({ flags }) => flags?.includes('showDripCampaigns') ?? false,
    handle: handleOverrideDripCampagns,
    description: 'Enables Drip campaigns for accounts with the showDripCampaigns flag',
  },
  {
    applies: ({ flags }) => flags?.includes('showProResources') ?? false,
    handle: handleOverrideProResources,
    description: 'Enables Pro Resources for accounts with the showProResources flag',
  },
];

export function applyExceptions({
  activeServices,
  license,
  flags,
}: IExceptionProps): TLicenseFeatures {
  return exceptionHandlers.reduce(
    (currentLicense, { applies, handle }) => {
      if (applies({ services: activeServices }) || flags.length > 0) {
        return handle({ activeServices, license: currentLicense, flags });
      }
      return currentLicense;
    },
    { ...license },
  );
}
